import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
} from "@/components/ui/field";
import { RequiredInput } from "@/components/common/required-input";
import { Separator } from "@/components/ui/separator";
import { DatePicker } from "@/components/common/date-picker";
import { useGetStaffLevelsPaginated } from "@/hooks/staff/use-staff-levels";
import { withStaffForm } from "@/hooks/staff/use-staff-form";
import { getStaffFormValues } from "@/default-form-values/staff/default-staff-form-values";
import { staffFormSchema } from "@/zod-schemas/staff/add-staff-schema";

interface StaffInfoStepProps {
  onNext: () => void;
  onCancel: () => void;
  serverError?: string | null;
  setServerError?: (error: string | null) => void;
}

export const StaffInfoStep = withStaffForm({
  defaultValues: getStaffFormValues(),
  validators: {
    onChange: staffFormSchema,
    onSubmit: staffFormSchema,
  },
  props: {} as StaffInfoStepProps,
  render: function StaffInfoStepRender(props) {
    const { form, onNext, onCancel, serverError, setServerError } = props;
    const { data: staffLevelsData } = useGetStaffLevelsPaginated({
      active_only: true,
    });
    const staffLevels = staffLevelsData?.items || [];

    return (
      <div className="space-y-6">
        <FieldGroup>
          <div className="col-span-2">
            <form.Field
              name="level_id"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Level
                      <RequiredInput />
                    </FieldLabel>
                    <Select
                      value={
                        Number(field.state.value ?? 0) > 0
                          ? String(field.state.value)
                          : ""
                      }
                      onValueChange={(value) => {
                        const numValue = parseInt(value);
                        field.handleChange(numValue);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {staffLevels.map(
                          (level: { id: number; name: string }) => (
                            <SelectItem
                              key={level.id}
                              value={level.id.toString()}
                            >
                              {level.name}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </div>
        </FieldGroup>

        <Separator className="my-4" />

        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <form.Field
              name="first_name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      First Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Enter first name"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="last_name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Last Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Enter last name"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="preferred_name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Preferred Name</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Enter preferred name"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="pronouns"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Pronouns</FieldLabel>
                    <Select
                      value={String(field.state.value ?? "")}
                      onValueChange={(value) => field.handleChange(value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="he/him">He/Him</SelectItem>
                        <SelectItem value="she/her">She/Her</SelectItem>
                        <SelectItem value="they/them">They/Them</SelectItem>
                        <SelectItem value="he/they">He/They</SelectItem>
                        <SelectItem value="she/they">She/They</SelectItem>
                      </SelectContent>
                    </Select>
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="phone"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Phone</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Enter phone number"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="email"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Email
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError?.(null);
                      }}
                      placeholder="Enter email address"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />
          </div>

          <form.Field
            name="address"
            children={(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Address</FieldLabel>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    value={String(field.state.value ?? "")}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Enter address"
                    rows={3}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          />
        </FieldGroup>

        <Separator className="my-4" />

        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <form.Field
              name="emergency_contact_name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Emergency Contact Name
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Enter emergency contact name"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="emergency_contact_phone"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Emergency Contact Phone
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={String(field.state.value ?? "")}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Enter emergency contact phone"
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <form.Field
              name="start_date"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Start Date
                      <RequiredInput />
                    </FieldLabel>
                    <DatePicker
                      id={field.name}
                      date={field.state.value as Date | null | undefined}
                      onDateChange={(date) => {
                        const dateValue = date ?? null;
                        (field.handleChange as (value: Date | null) => void)(dateValue);
                      }}
                      placeholder="Select..."
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="end_date"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>End Date</FieldLabel>
                    <DatePicker
                      id={field.name}
                      date={field.state.value as Date | null | undefined}
                      onDateChange={(date) => {
                        const dateValue = date ?? null;
                        (field.handleChange as (value: Date | null) => void)(dateValue);
                      }}
                      placeholder="Select..."
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <form.Field
              name="birthday"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Birthday</FieldLabel>
                    <DatePicker
                      id={field.name}
                      date={field.state.value as Date | null | undefined}
                      onDateChange={(date) => {
                        const dateValue = date ?? null;
                        (field.handleChange as (value: Date | null) => void)(dateValue);
                      }}
                      placeholder="Select..."
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </div>
        </FieldGroup>

        <form.Subscribe
          selector={(state) => {
            const { level_id, first_name, last_name, email, start_date } =
              state.values;
            return (
              level_id > 0 &&
              first_name.trim().length > 0 &&
              last_name.trim().length > 0 &&
              email.trim().length > 0 &&
              start_date !== null
            );
          }}
          children={(isInfoComplete) => (
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={onNext} disabled={!isInfoComplete}>
                Next
              </Button>
            </div>
          )}
        />
      </div>
    );
  },
});
